/**
 * Component: ChatMarkdown (widget)
 * SpecReferenceID: GALEN-CHAT-V1
 * Requirements: GALEN-CHAT-V1-R02, GALEN-CHAT-V1-R03, WIDGET-V1-R09
 *
 * Renders the streamed assistant response as markdown — the
 * widget analogue of FRONTEND-WEB-V1's MarkdownRenderer
 * (src/frontend/src/features/chat/components/MarkdownRenderer.tsx).
 * Same library (react-markdown + remark-gfm) so the widget and the
 * portal render the same model output identically, and the same
 * ``[docN]`` → inline superscript citation-ref transformation.
 *
 * Differences from the portal renderer (deliberately leaner — the
 * widget has no Tailwind/Radix and a tight bundle budget):
 *   - inline styles instead of `prose` classes;
 *   - no copy-to-clipboard code-block button (the model rarely
 *     emits code; not worth the lucide-react dep + UI);
 *   - the citation badge is a ``<sup>``-styled ``<a>`` that, on
 *     click, scrolls to ``#galen-cite-<n>`` in the references
 *     panel (or calls ``onCitationClick``).
 *
 * Risk Links: R11
 * FMEA Links: FM11
 */

import React, { useMemo } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export interface ChatMarkdownProps {
  content: string;
  /**
   * Number of citations available — enables the inline
   * ``[doc1]``..``[docN]`` → superscript-ref transformation
   * (an out-of-range ``[docN]`` is left as literal text).
   */
  citationCount?: number;
  /**
   * Optional callback when an inline citation badge is clicked
   * (1-based index). When omitted, the badge scrolls to
   * ``#galen-cite-<n>`` (the corresponding references-panel entry).
   */
  onCitationClick?: (citationNumber: number) => void;
}

/**
 * Convert ``[docN]`` markers in the source to markdown links
 * ``[N](#galen-cite-N)`` so react-markdown renders them as
 * anchors the custom ``a`` renderer below turns into citation
 * badges. (react-markdown v10 has no custom `text` component, so
 * the transform happens on the source string.) Out-of-range
 * indices are left untouched.
 */
function preprocessCitationRefs(content: string, citationCount: number): string {
  if (citationCount <= 0) return content;
  return content.replace(/\[doc(\d+)\]/g, (match, numStr: string) => {
    const num = parseInt(numStr, 10);
    if (!Number.isFinite(num) || num < 1 || num > citationCount) return match;
    return `[${num}](#galen-cite-${num})`;
  });
}

const CITATION_HREF_RE = /^#galen-cite-(\d+)$/;

export const ChatMarkdown: React.FC<ChatMarkdownProps> = ({
  content,
  citationCount = 0,
  onCitationClick,
}) => {
  const processed = useMemo(
    () => preprocessCitationRefs(content, citationCount),
    [content, citationCount],
  );

  return (
    <div
      data-galen-chat-markdown
      style={{
        fontSize: '0.95rem',
        lineHeight: 1.5,
        wordBreak: 'break-word',
      }}
    >
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          a({ href, children }) {
            const m = href ? CITATION_HREF_RE.exec(href) : null;
            if (m != null) {
              const num = parseInt(m[1] ?? '', 10);
              return (
                <a
                  data-galen-chat-cite-ref={num}
                  href={href}
                  aria-label={`Citation ${num}`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (onCitationClick) {
                      onCitationClick(num);
                      return;
                    }
                    document
                      .getElementById(`galen-cite-${num}`)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '1rem',
                    height: '1rem',
                    padding: '0 0.2rem',
                    marginLeft: '1px',
                    borderRadius: '3px',
                    verticalAlign: 'super',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    lineHeight: 1,
                    textDecoration: 'none',
                    color: 'var(--galen-primary, #2563eb)',
                    background:
                      'var(--galen-primary-soft, rgba(37, 99, 235, 0.12))',
                  }}
                >
                  {num}
                </a>
              );
            }
            // Ordinary link — open in a new tab with the safety pair.
            return (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            );
          },
          // Markdown can legitimately emit fenced code; render it
          // plainly (no copy button) so it still reads correctly.
          pre({ children }) {
            return (
              <pre
                style={{
                  overflowX: 'auto',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--galen-border-radius, 0.375rem)',
                  background: 'var(--galen-code-bg, #f1f5f9)',
                  fontSize: '0.85rem',
                }}
              >
                {children}
              </pre>
            );
          },
        }}
      >
        {processed}
      </Markdown>
    </div>
  );
};
